/**
 * P3 — Shared tank logistics (advisory only, NOT the stoichiometric solver).
 *
 * Used by:
 * - SaltCompatibilityAnalyzer (Stage 4 Configuration)
 * - ABTankSeparationEngine (post-solve tank split)
 *
 * Pascal stores ConcType A/B on substances; FlahaFast derives tanks from ions.
 * Same rules must apply in both UIs so partA/partB lists match.
 */

import { getSaltComposition, normalizeElementSymbol } from './saltElements';

export interface LogisticsSalt {
  id: string;
  name: string;
  formula?: string;
  elements?: any[];
}

export interface TankLogisticsIssue {
  saltA: string;
  saltB: string;
  severity: 'warning' | 'error';
  reason: string;
  recommendation: string;
}

export interface TankAssignment {
  partA: string[]; // salt ids
  partB: string[];
  /** id → 'A' | 'B' */
  tankById: Record<string, 'A' | 'B'>;
  issues: TankLogisticsIssue[];
  recommendations: string[];
  /** true when pair risks require separate tanks */
  recommendAB: boolean;
}

type IonRow = { symbol: string; percentage: number };

function flatElements(salt: LogisticsSalt): IonRow[] {
  const fromComp = getSaltComposition(salt).map((e) => ({
    symbol: e.symbol,
    percentage: e.percentage,
  }));
  if (fromComp.length > 0) return fromComp;

  // Fallback: formula heuristics when DB composition missing
  const f = (salt.formula || '').toUpperCase();
  const rows: IonRow[] = [];
  if (f.includes('CA')) rows.push({ symbol: 'Ca', percentage: 1 });
  if (f.includes('MG')) rows.push({ symbol: 'Mg', percentage: 1 });
  if (f.includes('FE')) rows.push({ symbol: 'Fe', percentage: 1 });
  if (f.includes('PO4') || (f.includes('P') && !f.includes('KNO3'))) {
    rows.push({ symbol: 'P', percentage: 1 });
  }
  if (f.includes('SO4') || (f.includes('S') && !f.includes('SI'))) {
    rows.push({ symbol: 'S', percentage: 1 });
  }
  if (f.includes('K')) rows.push({ symbol: 'K', percentage: 1 });
  return rows;
}

function hasIon(els: IonRow[], ion: string): boolean {
  const want = normalizeElementSymbol(ion) || ion;
  return els.some((e) => {
    const n = normalizeElementSymbol(e.symbol) || e.symbol;
    return n === want || e.symbol === ion;
  });
}

function pctOf(els: IonRow[], ion: string): number {
  const want = normalizeElementSymbol(ion) || ion;
  return els
    .filter((e) => {
      const n = normalizeElementSymbol(e.symbol) || e.symbol;
      return n === want || e.symbol === ion;
    })
    .reduce((s, e) => s + e.percentage, 0);
}

/** Pairwise precipitation risks (symmetric). */
export function getPairIssues(
  saltA: LogisticsSalt,
  saltB: LogisticsSalt
): TankLogisticsIssue[] {
  const issues: TankLogisticsIssue[] = [];
  const a = flatElements(saltA);
  const b = flatElements(saltB);

  const push = (
    cond: boolean,
    severity: 'warning' | 'error',
    reason: string,
    recommendation: string
  ) => {
    if (cond) {
      issues.push({
        saltA: saltA.name,
        saltB: saltB.name,
        severity,
        reason,
        recommendation,
      });
    }
  };

  push(
    (hasIon(a, 'Ca') && hasIon(b, 'P')) || (hasIon(b, 'Ca') && hasIon(a, 'P')),
    'error',
    'Calcium-phosphate precipitation risk',
    'Use Part A + Part B (separate stock tanks)'
  );

  {
    const both =
      (hasIon(a, 'Ca') && hasIon(b, 'S')) || (hasIon(b, 'Ca') && hasIon(a, 'S'));
    const ca = hasIon(a, 'Ca') ? pctOf(a, 'Ca') : pctOf(b, 'Ca');
    const s = hasIon(a, 'S') ? pctOf(a, 'S') : pctOf(b, 'S');
    push(
      both && ca > 15 && s > 10,
      'warning',
      'Calcium-sulfate precipitation risk at high concentrations',
      'Monitor stock strength or separate tanks'
    );
  }

  push(
    (hasIon(a, 'Fe') && hasIon(b, 'P')) || (hasIon(b, 'Fe') && hasIon(a, 'P')),
    'warning',
    'Iron-phosphate precipitation possible',
    'Prefer chelated Fe; keep Fe and phosphates in separate tanks when concentrated'
  );

  {
    const both =
      (hasIon(a, 'Mg') && hasIon(b, 'P')) || (hasIon(b, 'Mg') && hasIon(a, 'P'));
    const mg = hasIon(a, 'Mg') ? pctOf(a, 'Mg') : pctOf(b, 'Mg');
    const p = hasIon(a, 'P') ? pctOf(a, 'P') : pctOf(b, 'P');
    push(
      both && mg > 8 && p > 10,
      'warning',
      'Magnesium-phosphate precipitation risk',
      'Consider A/B separation at high concentration factor'
    );
  }

  return issues;
}

/**
 * Assign salt → tank A or B (logistics ConcType spirit).
 * A: Ca, Mg, Fe (without P), nitrates without P
 * B: phosphates, sulfates that pair poorly with Ca in concentrate
 */
export function assignSaltTank(salt: LogisticsSalt): 'A' | 'B' {
  const els = flatElements(salt);
  const hasCalcium = hasIon(els, 'Ca');
  const hasMagnesium = hasIon(els, 'Mg');
  const hasIron = hasIon(els, 'Fe');
  const hasPhosphate = hasIon(els, 'P');
  const hasSulfate = hasIon(els, 'S');

  // Phosphate-bearing salts → B (even if also have cations)
  if (hasPhosphate) return 'B';

  // Ca / Mg / Fe without phosphate → A
  if (hasCalcium || hasMagnesium || (hasIron && !hasPhosphate)) return 'A';

  // Sulfate without Ca path → B (keeps SO4 away from high-Ca A tank when split)
  if (hasSulfate) return 'B';

  return 'A'; // default nitrates/K to A
}

/**
 * Full logistics analysis for a salt set.
 */
export function analyzeTankLogistics(salts: LogisticsSalt[]): TankAssignment {
  const issues: TankLogisticsIssue[] = [];
  for (let i = 0; i < salts.length; i++) {
    for (let j = i + 1; j < salts.length; j++) {
      issues.push(...getPairIssues(salts[i], salts[j]));
    }
  }

  const partA: string[] = [];
  const partB: string[] = [];
  const tankById: Record<string, 'A' | 'B'> = {};

  // First pass: rule-based assignment
  const undecided: LogisticsSalt[] = [];
  for (const salt of salts) {
    const tank = assignSaltTank(salt);
    // Neutral-ish: neither strong A nor B ion → balance later
    const els = flatElements(salt);
    const strong =
      hasIon(els, 'Ca') ||
      hasIon(els, 'Mg') ||
      hasIon(els, 'Fe') ||
      hasIon(els, 'P') ||
      hasIon(els, 'S');
    if (!strong) {
      undecided.push(salt);
      continue;
    }
    tankById[salt.id] = tank;
    if (tank === 'A') partA.push(salt.id);
    else partB.push(salt.id);
  }

  // Balance remaining (KNO3 etc.) across tanks
  for (const salt of undecided) {
    const tank = partA.length <= partB.length ? 'A' : 'B';
    tankById[salt.id] = tank;
    if (tank === 'A') partA.push(salt.id);
    else partB.push(salt.id);
  }

  const recommendAB =
    issues.some((i) => i.severity === 'error') ||
    (partA.length > 0 && partB.length > 0 && issues.length > 0);

  const recommendations: string[] = [];
  if (issues.some((i) => i.severity === 'error')) {
    recommendations.push(
      'A/B stock system recommended — Ca and phosphates should not share one concentrated tank'
    );
  } else if (issues.length === 0) {
    recommendations.push(
      'No major pair risks — single stock is acceptable; A/B still optional for convenience'
    );
  }
  if (issues.some((i) => i.reason.toLowerCase().includes('iron'))) {
    recommendations.push('Prefer chelated iron (EDTA / DTPA / EDDHA) in concentrate');
  }
  recommendations.push(
    'This step is tank logistics only — salt grams come from the matrix solve (water + crop + salts)'
  );
  recommendations.push('Prepare stocks with pure water; store cool and dark');

  return {
    partA,
    partB,
    tankById,
    issues,
    recommendations,
    recommendAB,
  };
}

/** Partition salt objects into A/B using shared rules. */
export function partitionSaltsByTank<T extends LogisticsSalt>(
  salts: T[]
): { tankA: T[]; tankB: T[]; tankById: Record<string, 'A' | 'B'>; issues: TankLogisticsIssue[] } {
  const logistics = analyzeTankLogistics(salts);
  const tankA: T[] = [];
  const tankB: T[] = [];
  for (const s of salts) {
    const t = logistics.tankById[s.id] || assignSaltTank(s);
    if (t === 'A') tankA.push(s);
    else tankB.push(s);
  }
  return {
    tankA,
    tankB,
    tankById: logistics.tankById,
    issues: logistics.issues,
  };
}
